export const WORK_CAPABILITIES = [
    {
        id: "technology",
        builderId: "6f6d65c0eeaa423e9ff1608251b5763a",
    },
    {
        id: "creative",
        builderId: "c69fa5b7f31a4c328b2bb498ad91ca13",
    },
    {
        id: "marketing",
        builderId: "4b8d8daa12164f7886602f46c464f982",
    },
];

/** @type {Record<string, string>} */
const WORK_CAPABILITY_ID_ALIASES = {
    "technology-engineering": "technology",
    "technology + engineering": "technology",
    "technology + ecommerce": "technology",
    "technology-plus-engineering--1n1n9kl": "technology",
    "technology-plus-ecommerce--0dlc3ta": "technology",
    "creative-experience": "creative",
    "creative + experience": "creative",
    "creative + design": "creative",
    "creative-plus-experience--1bm3w17": "creative",
    "creative-plus-design--0yt7m9l": "creative",
    "marketing-performance": "marketing",
    "marketing + performance": "marketing",
    "marketing + growth": "marketing",
    "marketing-plus-performance--0i9afeo": "marketing",
    "marketing-plus-growth--0ylm91p": "marketing",
};

/**
 * @param {unknown} value
 */
function normalizeForLookup(value) {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * @param {unknown} value
 */
function normalizeEntityId(value) {
    return typeof value === "string" ? value.trim() : "";
}

/**
 * @param {unknown} value
 */
function toFallbackId(value) {
    const entityId = normalizeEntityId(value);

    if (!entityId) {
        return "";
    }

    const encodedEntityId = Array.from(new TextEncoder().encode(entityId), (byte) =>
        byte.toString(16).padStart(2, "0"),
    ).join("");

    return `service--${encodedEntityId}`;
}

/**
 * @param {{ id: unknown, name: unknown }[]} serviceEntities
 */
export function toWorkCapabilityOptions(serviceEntities) {
    const entitiesById = new Map();

    for (const serviceEntity of serviceEntities) {
        const id = normalizeEntityId(serviceEntity?.id);
        const name = serviceEntity?.name;

        if (id && typeof name === "string" && name.trim() && !entitiesById.has(id)) {
            entitiesById.set(id, name);
        }
    }

    const options = WORK_CAPABILITIES.flatMap(({ id, builderId }) => {
        const name = entitiesById.get(builderId);
        return name === undefined ? [] : [{ id, name }];
    });
    const knownBuilderIds = new Set(WORK_CAPABILITIES.map(({ builderId }) => builderId));

    for (const [builderId, name] of entitiesById) {
        if (!knownBuilderIds.has(builderId)) {
            options.push({ id: toFallbackId(builderId), name });
        }
    }

    return options;
}

/**
 * @param {unknown} value
 */
export function normalizeWorkCapabilityFilter(value) {
    const normalizedValue = normalizeForLookup(value);

    if (!normalizedValue) {
        return "";
    }

    const capability = WORK_CAPABILITIES.find(
        ({ id, builderId }) =>
            normalizeForLookup(id) === normalizedValue ||
            normalizeForLookup(builderId) === normalizedValue,
    );

    return capability?.id ?? WORK_CAPABILITY_ID_ALIASES[normalizedValue] ?? normalizedValue;
}

/**
 * @param {unknown} serviceEntityId
 * @param {unknown} filterValue
 */
export function workCapabilityMatches(serviceEntityId, filterValue) {
    const entityId = normalizeEntityId(serviceEntityId);
    const normalizedFilter = normalizeWorkCapabilityFilter(filterValue);
    const capability = WORK_CAPABILITIES.find(({ id }) => id === normalizedFilter);

    if (capability) {
        return entityId === capability.builderId;
    }

    return toFallbackId(entityId) === normalizedFilter;
}
